import { useEffect, useState } from "react";
import api, { apiError, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ExternalLink, Loader2, Ban, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_COLOR = { active: "bg-emerald-500/15 text-emerald-400", suspended: "bg-red-500/15 text-red-400" };

export default function CentralTenants() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", slug: "", owner_name: "", email: "", phone: "", country: "", city: "", address: "", plan: "Business", password: "boutique123", status: "active" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const load = () => api.get("/central/tenants").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      const slug = (f.slug || f.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      await api.post("/central/tenants", { ...f, slug });
      toast.success("Boutique créée");
      setOpen(false); setStep(1); load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const toggle = async (t) => {
    const status = t.status === "active" ? "suspended" : "active";
    try { await api.put(`/central/tenants/${t.id}`, { status }); load(); toast.success(status === "active" ? "Boutique activée" : "Boutique suspendue"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Boutiques" subtitle="Gérez toutes les boutiques clientes de la plateforme">
        <Button onClick={() => { setStep(1); setOpen(true); }} className="vp-accent-bg text-black font-medium" data-testid="tenant-create-btn"><Plus size={16} className="mr-1" /> Créer une boutique</Button>
      </PageHeader>

      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
              {["Boutique", "Propriétaire", "Sous-domaine", "Plan", "Statut", "Créée le", "Actions"].map((h) => <TableHead key={h} className="text-slate-400 text-xs uppercase tracking-wider">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</TableCell></TableRow>}
              {rows && rows.map((t) => (
                <TableRow key={t.id} className="border-white/5 hover:bg-white/[0.03]" data-testid="tenant-row">
                  <TableCell className="text-white font-medium">{t.name}</TableCell>
                  <TableCell className="text-slate-300 text-sm">{t.owner_name}<div className="text-xs text-slate-500">{t.email}</div></TableCell>
                  <TableCell className="mono text-xs text-slate-300">{t.slug}.vilopos.com</TableCell>
                  <TableCell><Badge variant="outline" className="border-white/15 text-slate-300">{t.plan}</Badge></TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[t.status] || "bg-slate-500/15 text-slate-400"}`}>{t.status === "active" ? "Active" : "Suspendue"}</span></TableCell>
                  <TableCell className="text-slate-400 text-sm">{fmtDate(t.created_at)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Link to={`/store/${t.slug}`} target="_blank"><Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-white" title="Voir la boutique"><ExternalLink size={15} /></Button></Link>
                    <Button size="icon" variant="ghost" onClick={() => toggle(t)} className="h-8 w-8 text-slate-400 hover:text-white" title="Activer/Suspendre" data-testid="tenant-toggle-btn">
                      {t.status === "active" ? <Ban size={15} /> : <CheckCircle size={15} />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Aucune boutique</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="vp-card border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Créer une boutique — Étape {step}/2</DialogTitle></DialogHeader>
          {step === 1 ? (
            <div className="space-y-4 py-2">
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Nom de la boutique</Label><Input value={f.name} onChange={set("name")} className="bg-black/30 border-white/10 text-white" data-testid="tenant-name" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Sous-domaine</Label>
                <div className="flex items-center"><Input value={f.slug} onChange={set("slug")} placeholder="ma-boutique" className="bg-black/30 border-white/10 text-white rounded-r-none" data-testid="tenant-slug" /><span className="px-3 py-2 text-sm text-slate-400 bg-black/40 border border-l-0 border-white/10 rounded-r-md">.vilopos.com</span></div></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300 text-sm mb-1.5 block">Propriétaire</Label><Input value={f.owner_name} onChange={set("owner_name")} className="bg-black/30 border-white/10 text-white" /></div>
                <div><Label className="text-slate-300 text-sm mb-1.5 block">Email</Label><Input value={f.email} onChange={set("email")} className="bg-black/30 border-white/10 text-white" data-testid="tenant-email" /></div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300 text-sm mb-1.5 block">Téléphone</Label><Input value={f.phone} onChange={set("phone")} className="bg-black/30 border-white/10 text-white" /></div>
                <div><Label className="text-slate-300 text-sm mb-1.5 block">Ville</Label><Input value={f.city} onChange={set("city")} className="bg-black/30 border-white/10 text-white" /></div>
                <div><Label className="text-slate-300 text-sm mb-1.5 block">Pays</Label><Input value={f.country} onChange={set("country")} className="bg-black/30 border-white/10 text-white" /></div>
                <div><Label className="text-slate-300 text-sm mb-1.5 block">Formule</Label>
                  <Select value={f.plan} onValueChange={(v) => setF({ ...f, plan: v })}><SelectTrigger className="bg-black/30 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="vp-card border-white/10 text-white">{["Starter", "Business", "Enterprise"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Mot de passe propriétaire</Label><Input value={f.password} onChange={set("password")} className="bg-black/30 border-white/10 text-white" /></div>
            </div>
          )}
          <DialogFooter>
            {step === 2 && <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-400">Précédent</Button>}
            {step === 1 ? <Button onClick={() => setStep(2)} className="vp-accent-bg text-black font-medium" data-testid="tenant-next-btn">Suivant</Button>
              : <Button onClick={create} disabled={saving} className="vp-accent-bg text-black font-medium" data-testid="tenant-save-btn">{saving && <Loader2 className="animate-spin mr-2" size={15} />} Créer la boutique</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
